/**
 * One change order, in full (2026-09-22).
 *
 * The list is a scan: number, title, money, status. Everything else — the
 * scope as written, the money broken down, which rooms, how it was approved,
 * internal notes — lives here, with the actions that apply to it.
 *
 * Custom markup rather than openModal(): this is a reading panel, not a form
 * (CLAUDE.md's exception), and it borrows the modal's look.
 */

import { esc, fmtDate } from '@/lib/dom';
import { CHANGE_ORDER_STATUS_LABEL } from '@/lib/data/change-orders';
import { changeOrderActions, approvalLine, signedMoney, canRestore } from '@/lib/change-order-ui';
import { changeOrderTotal } from '@/lib/change-order-budget';
import { buildHistory, type HistoryEntry } from '@/lib/change-order-history';
import {
  buildTimeline, questionThreads, unansweredCount, awaitingAnswerLabel,
  type TimelineEntry, type QuestionThread, type EventFields,
} from '@/lib/change-order-events';
import type {
  ChangeOrderRow, ChangeOrderRevisionRow, RoomRow,
} from '@/types/database';

/** What the list and the detail panel both do to a change order. */
export interface ChangeOrderActionHandlers {
  edit: (co: ChangeOrderRow) => void;
  send: (co: ChangeOrderRow) => Promise<void>;
  recordApproval: (co: ChangeOrderRow) => void;
  close: (co: ChangeOrderRow, as: 'rejected' | 'cancelled') => Promise<void>;
  /** Reopen an approved change order, keeping the version the client signed. */
  revise: (co: ChangeOrderRow) => Promise<void>;
  /**
   * 0046 — put a withdrawn or declined one back in play as a draft. Distinct
   * from `revise`: there is no approved version to file away.
   */
  reopen: (co: ChangeOrderRow) => Promise<void>;
  /** 0046 — move it to Deleted. Named `remove` because `delete` is a keyword. */
  remove: (co: ChangeOrderRow) => Promise<void>;
  restore: (co: ChangeOrderRow) => Promise<void>;
  /** 0047 — reply to a client's question. */
  answer: (co: ChangeOrderRow) => void;
}

export interface ChangeOrderDetailOptions {
  co: ChangeOrderRow;
  rooms: RoomRow[];
  /** Superseded versions of THIS change order (0041). */
  revisions?: ChangeOrderRevisionRow[];
  /**
   * The project's whole trail (0046) — filtered to this change order here, so
   * the caller hands over the workspace's array rather than a per-panel query.
   */
  events?: EventFields[];
  actions: ChangeOrderActionHandlers;
}

export function openChangeOrderDetail(opts: ChangeOrderDetailOptions): HTMLElement {
  const { co, rooms, actions } = opts;
  const history = buildHistory(opts.revisions ?? [], co);
  const events = opts.events ?? [];
  const timeline = buildTimeline(events, co.id);
  const threads = questionThreads(events, co.id);
  const waiting = awaitingAnswerLabel(unansweredCount(events, co.id));
  const a = changeOrderActions(co);
  const approved = approvalLine(co);
  const roomNames = co.affected_room_ids.length === 0
    ? 'All rooms'
    : co.affected_room_ids.map((id) => rooms.find((r) => r.id === id)?.name ?? 'A deleted room').join(', ');

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  overlay.innerHTML = `
    <div class="modal-card co-detail" role="dialog" aria-label="Change order ${co.number}">
      <div class="co-detail-head">
        <div>
          <div class="muted small">Change Order #${co.number}</div>
          <h2>${esc(co.title)}</h2>
        </div>
        <div class="co-detail-chips">
          <span class="chip chip-${esc(co.status)}">${esc(CHANGE_ORDER_STATUS_LABEL[co.status])}</span>
          ${co.revision > 0 ? `<span class="chip chip-revision">Revised ${co.revision}×</span>` : ''}
          ${co.deleted_at ? `<span class="chip chip-deleted">Deleted</span>` : ''}
        </div>
      </div>

      ${approved ? `<div class="co-detail-approved">${esc(approved)}</div>` : ''}

      <dl class="co-detail-money">
        <div><dt>Work</dt><dd>${esc(signedMoney(co.cost_delta_cents))}</dd></div>
        <div><dt>Design fee</dt><dd>${esc(signedMoney(co.designer_fee_cents))}</dd></div>
        <div class="co-detail-total"><dt>Total</dt><dd><strong>${esc(signedMoney(changeOrderTotal(co)))}</strong></dd></div>
      </dl>
      ${co.status === 'acknowledged'
        ? `<p class="muted small">Counted in the project’s revised budget.</p>`
        : co.status === 'sent'
          ? `<p class="muted small">Awaiting the client — not counted in the budget yet.</p>`
          : co.revision > 0 && co.status === 'draft'
            ? `<p class="muted small">Being revised — the approved version below no longer counts toward the
                 budget, and the client won’t see this one until you send it again.</p>`
            : `<p class="muted small">Not counted in the budget.</p>`}

      <div class="co-detail-block">
        <h3>Scope</h3>
        <p class="co-detail-scope">${esc(co.scope)}</p>
      </div>

      <div class="co-detail-block">
        <h3>Rooms</h3>
        <p>${esc(roomNames)}</p>
      </div>

      ${co.notes ? `
        <div class="co-detail-block">
          <h3>Internal notes <span class="muted small">— never shown to the client</span></h3>
          <p>${esc(co.notes)}</p>
        </div>` : ''}

      ${threads.length === 0 ? '' : `
        <div class="co-detail-block">
          <h3>The client asked${waiting ? ` <span class="chip chip-waiting">${esc(waiting)}</span>` : ''}</h3>
          ${threads.map(renderThread).join('')}
        </div>`}

      <div class="co-detail-block">
        <h3>History</h3>
        ${timeline.length === 0
          // A change order created before 0046 has no trail, so its own
          // columns are all there is. Newer ones never fall back here.
          ? `<ul class="co-detail-history">
               <li>Created ${esc(fmtDate(co.created_at))}</li>
               ${co.sent_at ? `<li>Sent to the client ${esc(fmtDate(co.sent_at))}</li>` : ''}
               ${co.acknowledged_at ? `<li>${esc(approved ?? 'Approved')}</li>` : ''}
               ${co.status === 'rejected' ? '<li>Declined by the client</li>' : ''}
               ${co.status === 'cancelled' ? '<li>Withdrawn</li>' : ''}
             </ul>`
          // The real trail (0046). It keeps the FIRST send, which `sent_at`
          // cannot: one timestamp, overwritten by a resend.
          : `<ul class="co-detail-history">${timeline.map(renderEvent).join('')}</ul>`}
      </div>

      ${history.length === 0 ? '' : `
        <div class="co-detail-block">
          <h3>Earlier versions <span class="muted small">— what the client approved before</span></h3>
          ${history.map(renderRevision).join('')}
        </div>`}

      <div class="modal-actions co-detail-actions">
        ${a.canSend ? `<button type="button" class="btn-primary" data-do="send">Send to client</button>` : ''}
        ${a.canRecordApproval ? `<button type="button" class="btn-link" data-do="approve">Mark approved</button>` : ''}
        ${a.canDecline ? `<button type="button" class="btn-link" data-do="decline">Declined</button>` : ''}
        ${a.canWithdraw ? `<button type="button" class="btn-link btn-danger-link" data-do="withdraw">Withdraw</button>` : ''}
        ${a.canEdit ? `<button type="button" class="btn-link" data-do="edit">Edit</button>` : ''}
        ${a.canRevise ? `<button type="button" class="btn-link" data-do="revise">Revise</button>` : ''}
        ${a.canReopen ? `<button type="button" class="btn-link" data-do="reopen">Reopen as draft</button>` : ''}
        ${threads.length > 0 ? `<button type="button" class="btn-link" data-do="answer">Answer the client</button>` : ''}
        ${a.canDelete ? `<button type="button" class="btn-link btn-danger-link" data-do="delete">Delete</button>` : ''}
        ${canRestore(co) ? `<button type="button" class="btn-primary" data-do="restore">Restore</button>` : ''}
        <span style="flex:1"></span>
        <button type="button" class="btn-link" data-do="close">Close</button>
      </div>
      ${co.deleted_at
        ? `<p class="muted small">In Deleted since ${esc(fmtDate(co.deleted_at))}. The client can’t see it.
             Restore it to work on it again.</p>`
        : a.canEdit ? '' : `<p class="muted small">${co.status === 'acknowledged'
          ? 'Approved and locked. Revising keeps this version and reopens it as a draft; a separate change is a new change order.'
          : 'Closed — reopen it as a draft to work on it again.'}</p>`}
    </div>`;

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  overlay.querySelectorAll<HTMLButtonElement>('[data-do]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const what = btn.dataset.do;
      // Every action either reloads the page behind this panel or opens its
      // own dialog, so the panel closes first either way.
      close();
      if (what === 'edit') actions.edit(co);
      else if (what === 'send') void actions.send(co);
      else if (what === 'approve') actions.recordApproval(co);
      else if (what === 'decline') void actions.close(co, 'rejected');
      else if (what === 'withdraw') void actions.close(co, 'cancelled');
      else if (what === 'revise') void actions.revise(co);
      else if (what === 'reopen') void actions.reopen(co);
      else if (what === 'delete') void actions.remove(co);
      else if (what === 'restore') void actions.restore(co);
      else if (what === 'answer') actions.answer(co);
    });
  });
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.body.append(overlay);
  document.addEventListener('keydown', onKey);
  return overlay;
}

/** One superseded version: what it was, who approved it, what changed after. */
function renderRevision(h: HistoryEntry): string {
  return `
    <div class="co-revision">
      <div class="co-revision-head">${esc(h.headline)}</div>
      <div class="muted small">${esc(h.approval)}</div>
      <div class="co-revision-title">${esc(h.revision.title)}</div>
      ${h.changes.length === 0
        ? `<div class="muted small">No changes recorded since.</div>`
        : `<ul class="co-revision-changes">${h.changes.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>`}
    </div>`;
}

/**
 * One line of the trail: what happened, when, and who by.
 *
 * No question or answer text — that is in the conversation block above, and
 * printing it in both put every question on the panel twice.
 */
function renderEvent(e: TimelineEntry): string {
  return `<li>${esc(e.headline)}${
    e.attribution ? ` <span class="muted small">${esc(e.attribution)}</span>` : ''}</li>`;
}

/** A client's question with whatever has been said back to it. */
function renderThread(t: QuestionThread): string {
  const asked = t.question;
  return `
    <div class="co-question">
      <div class="co-question-body">${esc(asked.note ?? '')}</div>
      <div class="muted small">${esc(asked.actor_name ?? 'The client')} · ${esc(fmtDate(asked.at))}</div>
      ${t.answers.length === 0
        ? `<div class="co-question-open muted small">Not answered yet.</div>`
        : t.answers.map((ans) => `
            <div class="co-answer">
              <div>${esc(ans.note ?? '')}</div>
              <div class="muted small">${esc(ans.actor_name ?? 'You')} · ${esc(fmtDate(ans.at))}</div>
            </div>`).join('')}
    </div>`;
}
